import { Modal, Pressable, View } from 'react-native';
import { useEffect } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Body, SubHeading, Caption } from '@/components/ui/Text';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { useUserStore } from '@/state/userStore';
import { useOfflinePlanTierDisplay } from '@/hooks/useOfflinePlanTierDisplay';

const DEFAULT_TITLE = 'Premium feature';
const DEFAULT_MESSAGE = 'Upgrade to Premium to unlock this feature.';

export type PremiumGateSheetContentProps = {
  title?: string;
  message?: string;
  feature?: string;
  onClose: () => void;
  onUpgrade: () => void;
};

/** Presentational paywall card (used by root `PremiumGateModal` and flows that stack their own RN `Modal`). */
export function PremiumGateSheetContent({
  title,
  message,
  feature,
  onClose,
  onUpgrade,
}: PremiumGateSheetContentProps) {
  const theme = useTheme();

  return (
    <Card variant="outlined" padding={theme.spacing(2.5)}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <View style={{ flex: 1, paddingRight: theme.spacing(2) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) }}>
            <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
            <SubHeading>{title || DEFAULT_TITLE}</SubHeading>
          </View>
          <Body color={theme.colors.textSecondary} style={{ lineHeight: 20 }}>
            {message || DEFAULT_MESSAGE}
          </Body>
          <View style={{ marginTop: theme.spacing(1.5), gap: theme.spacing(0.5) }}>
            {feature === 'email_sync_unlimited' ? (
              <Caption color={theme.colors.textSecondary}>
                Free: 1 active trip; one email import to capture bookings (does not reset if you delete a trip). Limited AI.
              </Caption>
            ) : (
              <Caption color={theme.colors.textSecondary}>Free: 1 active trip, limited AI/email sync</Caption>
            )}
            <Caption color={theme.colors.textSecondary}>Premium: unlimited trips, offline mode, unlimited AI/email sync</Caption>
          </View>
        </View>
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close"
          style={{ padding: theme.spacing(0.5) }}
        >
          <Ionicons name="close" size={22} color={theme.colors.textSecondary} />
        </Pressable>
      </View>

      <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), marginTop: theme.spacing(2) }}>
        <Button label="Not now" variant="secondary" onPress={onClose} style={{ flex: 1 }} />
        <Button label="Upgrade" onPress={onUpgrade} style={{ flex: 1 }} />
      </View>
    </Card>
  );
}

export function PremiumGateModal() {
  const theme = useTheme();
  const router = useRouter();
  // IMPORTANT:
  // Don't select an object slice here (e.g. `{ visible, title, ... }`), because returning a
  // brand-new object each render can cause React 18 external-store infinite re-render loops.
  const visible = usePremiumGateStore((s) => s.visible);
  const title = usePremiumGateStore((s) => s.title);
  const message = usePremiumGateStore((s) => s.message);
  const feature = usePremiumGateStore((s) => s.feature);
  const close = usePremiumGateStore((s) => s.close);
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const profile = useUserStore((s) => s.profile);
  const offlinePlan = useOfflinePlanTierDisplay(firebaseUser?.uid, {
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });

  useEffect(() => {
    if (!visible || !offlinePlan.effectivePremium) return;
    close();
  }, [visible, offlinePlan.effectivePremium, close]);

  // Avoid mounting RN Modal while hidden — cold-launch navigation storms can otherwise hit max update depth.
  if (!visible) {
    return null;
  }

  const handleUpgrade = () => {
    void logAnalyticsEvent('premium_paywall_started', {
      source: 'feature_gate',
      plan: 'premium',
    });

    close();
    // Open profile with upgrade flow so user can choose Subscribe (web/in-app) or Restore.
    router.replace('/(tabs)/profile?openUpgrade=1');
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <Pressable
        onPress={close}
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.45)',
          justifyContent: 'center',
          padding: theme.spacing(3),
        }}
      >
        <Pressable onPress={() => {}} style={{ width: '100%', maxWidth: 520, alignSelf: 'center' }}>
          <PremiumGateSheetContent
            title={title}
            message={message}
            feature={feature}
            onClose={close}
            onUpgrade={handleUpgrade}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}
